/**
 * Découpage d'un chemin de dossier en segments cliquables (navigation façon
 * EXPLORATEUR de fichiers).
 *
 * `rel_path` d'un dossier est un chemin POSIX (`2024/vacances/plage`). Pour bâtir un
 * fil d'Ariane de dossiers, on veut la liste de ses ANCÊTRES : chaque segment porte
 * son dernier nom (affiché) ET son chemin COMPLET jusqu'à lui inclus (cible de
 * navigation `?dossier=<chemin>`).
 *
 * Fonction PURE & testée, réutilisée par le fil d'Ariane du haut (`Breadcrumb`) et par
 * le fil d'Ariane de la vue Dossiers (`FoldersTabView`). La racine (`''`) → aucun
 * segment (le maillon « Dossiers » représente déjà la racine). Tolère les `/` de bord
 * ou doublés (segments vides ignorés).
 */

/** Un segment de chemin de dossier : nom affiché + chemin complet jusqu'à lui. */
export interface FolderSegment {
  /** Dernier segment du chemin (nom affiché du dossier). */
  name: string;
  /** Chemin COMPLET jusqu'à ce segment inclus (cible `?dossier=<path>`). */
  path: string;
}

/**
 * Décompose un chemin de dossier en ses segments ancêtres, du plus haut au plus bas.
 * `''` (racine) ⇒ `[]`. Ex. `2024/vacances` ⇒ `[{name:'2024',path:'2024'},
 * {name:'vacances',path:'2024/vacances'}]`.
 */
export function folderPathSegments(path: string): FolderSegment[] {
  if (path === '') return [];
  const segments: FolderSegment[] = [];
  let acc = '';
  for (const part of path.split('/')) {
    if (part === '') continue; // tolère un `/` de bord ou doublé
    acc = acc === '' ? part : `${acc}/${part}`;
    segments.push({ name: part, path: acc });
  }
  return segments;
}

/** Chemin de FICHIER décomposé : dossiers ancêtres cliquables + nom du fichier. */
export interface FilePathSegments {
  /** Dossiers ancêtres du fichier, du plus haut au plus bas (`[]` si à la racine). */
  folders: FolderSegment[];
  /** Dernier segment du chemin (nom du fichier) ; `''` pour une entrée dégénérée. */
  fileName: string;
}

/**
 * Décompose le `rel_path` COMPLET d'un asset (dossiers + nom de fichier) en segments
 * cliquables. Variante « fichier » de `folderPathSegments` : le DERNIER segment est le
 * nom du fichier (rendu à part — ce n'est pas une cible `?dossier=`), les précédents
 * sont ses dossiers ancêtres avec leur chemin cumulé.
 *
 * Ex. `Photos/2012/plage/img.jpg` ⇒ `folders` = `Photos`, `Photos/2012`,
 * `Photos/2012/plage` ; `fileName` = `img.jpg`. Fichier à la RACINE (`img.jpg`) ⇒
 * aucun dossier. Entrée dégénérée (`''`, `'/'`) ⇒ tout vide, pas de crash. Tolère les
 * `/` de bord ou doublés (aucun segment vide).
 *
 * Cohérent avec `dirnameOf` (`viewer/format.ts`) : `folders` décrit exactement le même
 * dossier parent, découpé.
 */
export function filePathSegments(relPath: string): FilePathSegments {
  const parts = relPath.split('/').filter((part) => part !== '');
  return {
    folders: folderPathSegments(parts.slice(0, -1).join('/')),
    fileName: parts.at(-1) ?? '',
  };
}
